import React, { CSSProperties, ReactNode, useMemo } from "react";
import styled from "@emotion/styled";

import { onClickHandler } from "../types";
import useAppThemePalette from "../../../shared/hooks/useAppThemePalette";
import { Box, SvgIcon } from "@mui/material";
import { getCSSTransition } from "../../../shared/functions/colorsMath";

export const enum IconType {
  UStorkLogo = 0,
  Archive,
  Calendar,
  Plus,
  Minus,
  Search,
  Stars,
  Ellipsis,
  Check,
  Cross,
  User,
  Clock,
  Chat,
  Image,
  Video,
  File,
  Edit,
  Embedded,
  Subheader,
  TrashBin,
  FontBoldUA,
  FontBoldEN,
  FontItalicUA,
  FontItalicEN,
  WebLink,
  ListBulleted,
  ListNumbered,
  InfoHide,
  InfoShow,
  ViewChart,
  ViewCards,
  ArrowLongLeft,
  ArrowLongRight,
  ArrowLongLBRT,
  ArrowheadUp,
  ArrowheadDown,
  ArrowheadRight,
  ArrowheadLeft,
  ArrowReverseRight,
}

interface IconTypedProps {
  viewBox?: string;
  path: JSX.Element;
}

const _icons: Record<IconType, IconTypedProps> = {
  [IconType.UStorkLogo]: {
    viewBox: "0 0 133 31",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M117.389 1.31c0 .415-.312.72-.776.72s-.776-.305-.776-.697c0-.392.312-.696.776-.696s.776.288.776.672zm-.152 1.32v4.303h-1.248V2.63h1.248zm4.295.455V.997h1.248v5.936h-1.192v-.496c-.312.376-.768.56-1.344.56-1.216 0-2.152-.864-2.152-2.216 0-1.352.936-2.216 2.152-2.216.528 0 .976.168 1.288.52zm-2.176 1.696c0 .744.48 1.192 1.104 1.192.616 0 1.096-.448 1.096-1.192s-.48-1.192-1.096-1.192c-.624 0-1.104.448-1.104 1.192zm8.795.016c0-1.384-.976-2.232-2.24-2.232-1.312 0-2.296.928-2.296 2.216 0 1.28.968 2.216 2.456 2.216.776 0 1.376-.24 1.776-.696l-.664-.72c-.296.28-.624.416-1.08.416-.656 0-1.112-.328-1.232-.864h3.256l.008-.095v-.002c.008-.083.016-.17.016-.239zm-2.232-1.288c.56 0 .968.352 1.056.896h-2.12c.088-.552.496-.896 1.064-.896zm2.825-.424c.472-.336 1.168-.52 1.856-.52 1.312 0 2.072.608 2.072 1.912v2.456h-1.168v-.536c-.232.392-.68.6-1.312.6-1.008 0-1.608-.56-1.608-1.304 0-.76.536-1.288 1.848-1.288h.992c0-.536-.32-.848-.992-.848-.456 0-.928.152-1.24.4l-.448-.872zm1.056 2.552c0 .312.264.52.704.52.416 0 .776-.192.92-.584v-.44h-.856c-.584 0-.768.216-.768.504zM64.464 10c-2.985 0-5.543 1.014-7.675 3.066-2.11 2.029-3.165 4.494-3.165 7.434 0 2.94 1.054 5.432 3.136 7.46 2.108 2.026 4.671 3.04 7.652 3.04 2.98 0 5.539-1.014 7.622-3.066 2.111-2.052 3.166-4.577 3.166-7.512 0-2.936-1.053-5.407-3.139-7.408-2.085-2-4.612-3.014-7.597-3.014zm-3.96 14.503a5.76 5.76 0 01-1.583-4.03h.004a5.651 5.651 0 011.58-4 5.298 5.298 0 011.771-1.25 5.368 5.368 0 014.264 0 5.299 5.299 0 011.772 1.25 5.651 5.651 0 011.583 4 5.76 5.76 0 01-1.583 4.03c-.496.532-1.1.957-1.772 1.248a5.368 5.368 0 01-4.264 0 5.298 5.298 0 01-1.772-1.248zm-46.848 2.312a2.922 2.922 0 00-.219 1.183h.009a2.8 2.8 0 00.502 1.647c-1.368.875-3.166 1.342-5.401 1.342C2.77 30.987 0 27.765 0 22.67V10.35h5.25v12.243c0 2.156 1.133 3.612 3.35 3.612 2.4 0 3.376-1.508 3.376-3.508V10.35h5.24v12.243c.003.789-.073 1.576-.227 2.35a3.505 3.505 0 00-.491-.039 2.993 2.993 0 00-1.178.234 2.955 2.955 0 00-.986.676 2.958 2.958 0 00-.678 1zm13.948 4.172c2.506 0 4.458-.597 5.856-1.793h.009c1.397-1.196 2.083-2.729 2.083-4.575 0-1.658-.606-2.963-1.793-3.898-1.187-.962-3.086-1.82-5.724-2.573-2.19-.624-2.797-1.222-2.797-2.079 0-1.015.924-1.715 2.215-1.715 1.66 0 2.823.805 3.508 2.416l4.249-2.287c-1.161-2.702-3.826-4.47-7.624-4.47-2.084 0-3.852.597-5.329 1.793a5.655 5.655 0 00-1.638 2.02 5.569 5.569 0 00-.55 2.529c0 3.404 2.373 5.25 7.015 6.497 2.453.65 3.218 1.17 3.218 2.079 0 1.04-1.002 1.69-2.664 1.69-1.978 0-3.35-.832-4.115-2.495l-4.305 2.183c1.343 3.119 4.138 4.678 8.386 4.678zm8.925-15.983h5.856v15.698h5.2V15.004h5.827V10.35H36.529v4.654zm40.73 15.698h5.198v-7.149h1.687l4.142 7.154h5.646l-4.59-7.926c2.454-.864 4.115-2.761 4.115-5.88 0-2.158-.713-3.77-2.11-4.888-1.398-1.117-3.271-1.663-5.62-1.663H77.26v20.352zm8.074-11.515h-2.876l.006-4.413h2.843c2.03 0 2.928.935 2.928 2.313 0 1.347-1.03 2.1-2.901 2.1zm10.445-8.837v20.352H101v-9.643l6.538 9.643H114l-7.597-10.682 7.095-9.67h-6.112l-6.384 8.733V10.35h-5.224z'
        fill='#000'
      />
    ),
  },
  [IconType.Archive]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4 4h24v4H4V4zm0 6a2 2 0 01-2-2V4a2 2 0 012-2h24a2 2 0 012 2v4a2 2 0 01-2 2v16a4 4 0 01-4 4H8a4 4 0 01-4-4V10zm2 0v16a2 2 0 002 2h16a2 2 0 002-2V10H6zm4 5a1 1 0 011-1h10a1 1 0 110 2H11a1 1 0 01-1-1z'
        fill='#000'
      />
    ),
  },
  [IconType.Calendar]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M16 8a2 2 0 100-4 2 2 0 000 4zM4 14a2 2 0 100-4 2 2 0 000 4zm0 6a2 2 0 100-4 2 2 0 000 4zm2 4a2 2 0 11-4 0 2 2 0 014 0zm4-10a2 2 0 100-4 2 2 0 000 4zm2 4a2 2 0 11-4 0 2 2 0 014 0zm-2 8a2 2 0 100-4 2 2 0 000 4zm8-2a2 2 0 11-4 0 2 2 0 014 0zm0-12a2 2 0 11-4 0 2 2 0 014 0zm-2 8a2 2 0 100-4 2 2 0 000 4zm8-14a2 2 0 11-4 0 2 2 0 014 0zm-2 8a2 2 0 100-4 2 2 0 000 4zm2 4a2 2 0 11-4 0 2 2 0 014 0zm4-10a2 2 0 100-4 2 2 0 000 4zm2 4a2 2 0 11-4 0 2 2 0 014 0zm-2 8a2 2 0 100-4 2 2 0 000 4z'
        fill='#000'
      />
    ),
  },
  [IconType.Plus]: {
    viewBox: "0 0 30 30",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M16.5 2a1.5 1.5 0 00-3 0v11.5H2a1.5 1.5 0 000 3h11.5V28a1.5 1.5 0 003 0V16.5H28a1.5 1.5 0 000-3H16.5V2z'
        fill='#000'
      />
    ),
  },
  [IconType.Minus]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M3 14.5a1.5 1.5 0 000 3h26a1.5 1.5 0 000-3H3z'
        fill='#000'
      />
    ),
  },
  [IconType.Search]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M24 14c0 5.523-4.477 10-10 10S4 19.523 4 14 8.477 4 14 4s10 4.477 10 10zm-2.251 9.163A11.952 11.952 0 0114 26C7.373 26 2 20.627 2 14S7.373 2 14 2s12 5.373 12 12c0 2.954-1.067 5.658-2.837 7.749l6.544 6.544a1 1 0 01-1.414 1.414l-6.544-6.544z'
        fill='#000'
      />
    ),
  },
  [IconType.Stars]: {
    viewBox: "0 0 34 34",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M22.12 8.732a.4.4 0 00.58.421l2.035-1.07a.4.4 0 01.373 0l2.035 1.07a.4.4 0 00.58-.421l-.388-2.267a.4.4 0 01.115-.354l1.646-1.605a.4.4 0 00-.221-.682l-2.276-.33a.4.4 0 01-.301-.22L25.28 1.212a.4.4 0 00-.717 0l-1.018 2.062a.4.4 0 01-.301.22l-2.276.33a.4.4 0 00-.221.682l1.646 1.605a.4.4 0 01.115.354l-.388 2.267zm-10.213 0a.4.4 0 01-.58.421L9.29 8.083a.4.4 0 00-.372 0l-2.036 1.07a.4.4 0 01-.58-.421l.389-2.267a.4.4 0 00-.115-.354L4.93 4.506a.4.4 0 01.222-.682l2.275-.33a.4.4 0 00.301-.22l1.018-2.062a.4.4 0 01.717 0l1.018 2.062a.4.4 0 00.301.22l2.276.33a.4.4 0 01.222.682l-1.647 1.605a.4.4 0 00-.115.354l.389 2.267zm6.887-3.92c-.734-1.486-2.853-1.486-3.587 0l-2.63 5.33-5.88.854c-1.64.238-2.295 2.254-1.108 3.411l4.255 4.148-1.005 5.856c-.28 1.634 1.435 2.88 2.902 2.108l5.26-2.765 5.259 2.765c1.467.772 3.182-.474 2.902-2.108l-1.005-5.857 4.255-4.147c1.187-1.157.532-3.173-1.108-3.411l-5.88-.855-2.63-5.328zm-4.423 6.214L17 5.698l2.63 5.328a2 2 0 001.505 1.094l5.88.855-4.255 4.147a2 2 0 00-.575 1.77l1.005 5.857-5.26-2.765a2 2 0 00-1.861 0l-5.26 2.765 1.005-5.856a2 2 0 00-.575-1.77l-4.255-4.148 5.88-.855a2 2 0 001.506-1.094zm2.27 14.17a.4.4 0 01.718 0l1.018 2.062a.4.4 0 00.3.22l2.276.33a.4.4 0 01.222.682l-1.646 1.605a.4.4 0 00-.116.354l.39 2.267a.4.4 0 01-.581.422l-2.036-1.07a.4.4 0 00-.372 0l-2.035 1.07a.4.4 0 01-.58-.422l.388-2.267a.4.4 0 00-.115-.354l-1.647-1.605a.4.4 0 01.222-.682l2.276-.33a.4.4 0 00.3-.22l1.019-2.062zm-8.083-5.978a.4.4 0 00-.222-.682l-2.275-.33a.4.4 0 01-.301-.22l-1.018-2.062a.4.4 0 00-.718 0l-1.017 2.062a.4.4 0 01-.301.22l-2.276.33a.4.4 0 00-.222.682l1.647 1.605a.4.4 0 01.115.354l-.389 2.267a.4.4 0 00.58.422l2.036-1.07a.4.4 0 01.372 0l2.036 1.07a.4.4 0 00.58-.422l-.389-2.267a.4.4 0 01.115-.354l1.647-1.605zm16.884 0a.4.4 0 01.221-.682l2.276-.33a.4.4 0 00.301-.22l1.018-2.062a.4.4 0 01.718 0l1.017 2.063a.4.4 0 00.301.218l2.276.331a.4.4 0 01.222.682l-1.647 1.605a.4.4 0 00-.115.354l.389 2.267a.4.4 0 01-.58.422l-2.036-1.07a.4.4 0 00-.372 0l-2.036 1.07a.4.4 0 01-.58-.422l.389-2.267a.4.4 0 00-.116-.354l-1.646-1.605z'
        fill='#000'
      />
    ),
  },
  [IconType.Ellipsis]: {
    path: (
      <path
        d='M8 16a4 4 0 11-8 0 4 4 0 018 0zM20 16a4 4 0 11-8 0 4 4 0 018 0zM32 16a4 4 0 11-8 0 4 4 0 018 0z'
        fill='#000'
      />
    ),
  },
  [IconType.Check]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M27.728 7.008a2 2 0 00-3.456-2.015L13.845 22.869l-6.506-5.855a2 2 0 00-2.676 2.973l8.333 7.5a2 2 0 003.066-.479l11.667-20z'
        fill='#000'
      />
    ),
  },
  [IconType.Cross]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M7.06 4.94a1.5 1.5 0 10-2.12 2.12L13.878 16l-8.94 8.94a1.5 1.5 0 002.122 2.12L16 18.122l8.94 8.94a1.5 1.5 0 002.12-2.122L18.122 16l8.94-8.94a1.5 1.5 0 00-2.122-2.12L16 13.878l-8.94-8.94z'
        fill='#000'
      />
    ),
  },
  [IconType.User]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M19.877 8a4 4 0 11-8 0 4 4 0 018 0zm2 0a6 6 0 11-12 0 6 6 0 0112 0zM16 18c5.523 0 10 4.477 10 10H6c0-5.523 4.477-10 10-10zm11.945 11.161c-.047.486-.469.839-.957.839H5.012c-.488 0-.91-.353-.957-.839C4.02 28.78 4 28.391 4 28c0-6.627 5.373-12 12-12s12 5.373 12 12c0 .392-.019.78-.055 1.161z'
        fill='#000'
      />
    ),
  },
  [IconType.Clock]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M32 16c0 8.837-7.163 16-16 16S0 24.837 0 16 7.163 0 16 0s16 7.163 16 16zm-2 0c0 7.732-6.268 14-14 14S2 23.732 2 16 8.268 2 16 2s14 6.268 14 14zM14 4v14h10v-2h-8V4h-2z'
        fill='#000'
      />
    ),
  },
  [IconType.Chat]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M32 15.313c0-3.272-1.93-6.125-4.827-8.117C24.272 5.202 20.318 4 16 4S7.728 5.202 4.827 7.196C1.93 9.188 0 12.041 0 15.313c0 2.464 1.1 4.7 2.881 6.5L1.3 27.085c-.377 1.257 1.026 2.295 2.117 1.567l5.033-3.355c2.255.85 4.828 1.327 7.551 1.327 4.318 0 8.272-1.202 11.173-3.196C30.07 21.437 32 18.584 32 15.313zm-30 0c0-2.424 1.428-4.728 3.96-6.469C8.487 7.107 12.034 6 16 6c3.966 0 7.513 1.107 10.04 2.845 2.532 1.74 3.96 4.044 3.96 6.467 0 2.424-1.428 4.728-3.96 6.469-2.527 1.737-6.074 2.844-10.04 2.844-2.601 0-5.028-.477-7.106-1.299a1.405 1.405 0 00-1.296.136l-3.905 2.603L4.9 22.042a1.408 1.408 0 00-.38-1.424C2.892 19.069 2 17.233 2 15.314z'
        fill='#000'
      />
    ),
  },
  [IconType.Image]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M28 6H4a2 2 0 00-2 2v10.857l3.185-3.64a4 4 0 015.838-.194L18 22l2.354-2.354a4 4 0 015.462-.182L30 23.125V8a2 2 0 00-2-2zM4 4a4 4 0 00-4 4v16a4 4 0 004 4h24a4 4 0 004-4V8a4 4 0 00-4-4H4zm19 10a3 3 0 100-6 3 3 0 000 6z'
        fill='#000'
      />
    ),
  },
  [IconType.Video]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4 6h24a2 2 0 012 2v16a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zM0 8a4 4 0 014-4h24a4 4 0 014 4v16a4 4 0 01-4 4H4a4 4 0 01-4-4V8zm13.514 2.909l7.057 4.233a1 1 0 010 1.716l-7.057 4.233A1 1 0 0112 20.234v-8.468a1 1 0 011.514-.857z'
        fill='#000'
      />
    ),
  },
  [IconType.File]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M6 4a2 2 0 012-2h9v7a2 2 0 002 2h7v17a2 2 0 01-2 2H8a2 2 0 01-2-2V4zm18.586 5L19 3.414V9h5.586zM8 0a4 4 0 00-4 4v24a4 4 0 004 4h16a4 4 0 004-4V9.586l-.293-.293-9-9L18.414 0H8z'
        fill='#000'
      />
    ),
  },
  [IconType.Edit]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M20.729 2.884l3.464 2-11.807 20.451-4.232 3.33.768-5.33L20.729 2.884zM7.426 31.783l6.5-5.114 12-20.785a2 2 0 00-.733-2.732l-3.464-2a2 2 0 00-2.732.732l-12 20.785-1.18 8.185c-.128.894.9 1.487 1.609.929z'
        fill='#000'
      />
    ),
  },
  [IconType.Embedded]: {
    viewBox: "0 0 38 32",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M23.928 6.371a1 1 0 10-1.857-.742l-8 20a1 1 0 001.857.742l8-20zm-12.303 2.41a1 1 0 00-1.25-1.562l-9.024 7.22a2 2 0 000 3.123l9.024 7.219a1 1 0 001.25-1.562L2.6 16l9.024-7.22zm16-1.562a1 1 0 00-1.25 1.562L35.4 16l-9.024 7.22a1 1 0 101.25 1.56l9.024-7.218a2 2 0 000-3.124L27.625 7.22z'
        fill='#000'
      />
    ),
  },
  [IconType.Subheader]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4 2a2 2 0 00-2 2v2a2 2 0 002 2h24a2 2 0 002-2V4a2 2 0 00-2-2H4zM3 12a1 1 0 100 2h26a1 1 0 100-2H3zm0 4a1 1 0 100 2h26a1 1 0 100-2H3zm0 4a1 1 0 100 2h26a1 1 0 100-2H3zm0 4a1 1 0 100 2h26a1 1 0 100-2H3zm0 4a1 1 0 100 2h18a1 1 0 100-2H3z'
        fill='#000'
      />
    ),
  },
  [IconType.TrashBin]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M14 0a3 3 0 00-2.4 1.2L9.5 4H1a1 1 0 000 2h3.074L5.79 28.307A4 4 0 009.778 32h12.444a4 4 0 003.988-3.693L27.926 6H31a1 1 0 100-2h-8.5l-2.1-2.8A3 3 0 0018 0h-4zm6 4h-8l1.2-1.6A1 1 0 0114 2h4a1 1 0 01.8.4L20 4zM7.784 28.153L6.08 6h19.84l-1.704 22.153A2 2 0 0122.222 30H9.778a2 2 0 01-1.994-1.847zm3.923-15.86a1 1 0 00-1.414 1.414L14.586 18l-4.293 4.293a1 1 0 101.414 1.414L16 19.414l4.293 4.293a1 1 0 001.414-1.414L17.414 18l4.293-4.293a1 1 0 00-1.414-1.414L16 16.586l-4.293-4.293z'
        fill='#000'
      />
    ),
  },
  [IconType.FontBoldUA]: {
    viewBox: "0 0 36 32",
    path: (
      <path
        d='M27.351 28H36l-5.968-12.617L35.68 4H27.83l-4.053 8.949h-2.075V4h-7.34v8.949H12.16L8.17 4H.32L6 15.383 0 28h8.649l3.702-8.674h2.01V28h7.341v-8.674h1.947L27.35 28z'
        fill='#000'
      />
    ),
  },
  [IconType.FontBoldEN]: {
    path: (
      <path
        d='M7 28V4.001h8.344c1.295-.015 2.587.12 3.853.404a8.333 8.333 0 012.88 1.229A5.485 5.485 0 0123.878 7.7c.433.909.647 1.913.623 2.925a7.064 7.064 0 01-.173 1.592 5.112 5.112 0 01-.569 1.44c-.268.46-.609.87-1.009 1.213a5.15 5.15 0 01-1.516.882 5.235 5.235 0 011.755.808c.458.329.853.742 1.165 1.22.295.456.513.96.645 1.491.133.53.2 1.076.201 1.624 0 2.372-.734 4.15-2.203 5.332C21.327 27.41 19.153 28 16.272 28H7zm5.599-13.985h2.92c1.224-.01 2.096-.242 2.617-.692.52-.45.78-1.115.782-1.994 0-1.012-.282-1.74-.846-2.185-.564-.445-1.474-.669-2.728-.672h-2.745v5.543zm0 3.79v5.753h3.67c.49.012.978-.06 1.445-.214.36-.12.692-.32.972-.585.247-.244.436-.544.55-.876a3.16 3.16 0 00.174-1.073 4.544 4.544 0 00-.16-1.252 2.347 2.347 0 00-.519-.948 2.33 2.33 0 00-.909-.6 3.79 3.79 0 00-1.356-.215l-3.867.01z'
        fill='#000'
      />
    ),
  },
  [IconType.FontItalicUA]: {
    path: (
      <path
        d='M16.328 15.143L28 4h-5.118L10.179 16.137 12.396 4H8.37L4 28h4.026l1.124-6.137 4.15-3.977L19.511 28h4.525l-7.77-12.823.062-.034z'
        fill='#000'
      />
    ),
  },
  [IconType.FontItalicEN]: {
    path: <path d='M15.782 28H13l3.763-24h2.782l-3.763 24z' fill='#000' />,
  },
  [IconType.WebLink]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M7.515 14.586A1 1 0 118.929 16l-3.536 3.535a5 5 0 007.071 7.072L16 23.07a1 1 0 111.414 1.414l-3.535 3.536a7 7 0 11-9.9-9.9l3.536-3.535zM23.07 16a1 1 0 001.414 1.414l3.536-3.535a7 7 0 10-9.9-9.9l-3.535 3.535A1 1 0 1016 8.93l3.535-3.536a5 5 0 017.072 7.071L23.07 16zM9.636 20.95a1 1 0 101.414 1.414L22.364 11.05a1 1 0 00-1.414-1.414L9.636 20.95z'
        fill='#000'
      />
    ),
  },
  [IconType.ListBulleted]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4 10a2 2 0 100-4 2 2 0 000 4zm7-3a1 1 0 100 2h18a1 1 0 100-2H11zm0 8a1 1 0 100 2h18a1 1 0 100-2H11zm-1 9a1 1 0 011-1h18a1 1 0 110 2H11a1 1 0 01-1-1zm-4-8a2 2 0 11-4 0 2 2 0 014 0zM4 26a2 2 0 100-4 2 2 0 000 4z'
        fill='#000'
      />
    ),
  },
  [IconType.ListNumbered]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M2.85 5.3a.7.7 0 000 1.4h1.3v2.6h-1.3a.7.7 0 000 1.4h4a.7.7 0 000-1.4h-1.3v-4h-2.7zm8 1.7a1 1 0 100 2h18a1 1 0 100-2h-18zm0 8a1 1 0 100 2h18a1 1 0 100-2h-18zm-1 9a1 1 0 011-1h18a1 1 0 110 2h-18a1 1 0 01-1-1zm-7-2.7a.7.7 0 000 1.4h3a.3.3 0 01.3.3v.3h-1.3a.7.7 0 000 1.4h1.3v.3a.3.3 0 01-.3.3h-3a.7.7 0 000 1.4h3a1.7 1.7 0 001.7-1.7v-2a1.7 1.7 0 00-1.7-1.7h-3zm-.7-7.3a.7.7 0 01.7-.7h3.382a1.318 1.318 0 01.59 2.497L3.814 17.3H6.85a.7.7 0 110 1.4h-4.7V17.618a1.7 1.7 0 01.94-1.52L5.885 14.7H2.85a.7.7 0 01-.7-.7z'
        fill='#000'
      />
    ),
  },
  [IconType.InfoHide]: {
    viewBox: "0 0 36 32",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M32.707 2.707a1 1 0 00-1.414-1.414L26.1 6.487C23.665 5.03 20.913 4 18 4 9.9 4 3.05 11.965.79 14.917a1.769 1.769 0 000 2.166c1.252 1.634 3.909 4.803 7.405 7.308l-4.902 4.902a1 1 0 101.415 1.414l28-28zM9.63 22.957l3.48-3.48a6 6 0 018.367-8.367l3.16-3.16C22.541 6.766 20.293 6 18 6c-3.405 0-6.71 1.688-9.56 3.925C5.704 12.073 3.59 14.567 2.48 16c1.11 1.433 3.224 3.927 5.96 6.075.389.305.786.6 1.19.881zm14.274-8.033l-6.98 6.98a6 6 0 006.98-6.98zM18 26c-1.468 0-2.918-.314-4.321-.85l-1.531 1.531C13.982 27.491 15.95 28 18 28c8.1 0 14.95-7.965 17.21-10.917a1.769 1.769 0 000-2.166c-1.012-1.32-2.941-3.644-5.49-5.809l-1.419 1.42c2.377 1.996 4.212 4.171 5.218 5.472-1.108 1.433-3.223 3.927-5.96 6.075C24.71 24.312 21.406 26 18 26z'
        fill='#000'
      />
    ),
  },
  [IconType.InfoShow]: {
    viewBox: "0 0 36 32",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M27.56 9.925c2.736 2.148 4.85 4.642 5.96 6.075-1.11 1.433-3.224 3.927-5.96 6.075C24.71 24.312 21.405 26 18 26c-3.405 0-6.71-1.688-9.56-3.925C5.704 19.927 3.59 17.433 2.48 16c1.11-1.433 3.224-3.927 5.96-6.075C11.29 7.688 14.595 6 18 6c3.405 0 6.71 1.688 9.56 3.925zm7.65 4.992a1.769 1.769 0 010 2.166C32.95 20.035 26.1 28 18 28 9.9 28 3.05 20.035.79 17.083a1.769 1.769 0 010-2.166C3.05 11.965 9.9 4 18 4c8.1 0 14.95 7.965 17.21 10.917zM18 22a6 6 0 100-12 6 6 0 000 12z'
        fill='#000'
      />
    ),
  },
  [IconType.ViewChart]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M0 6a2 2 0 012-2h20a2 2 0 012 2v2a2 2 0 01-2 2H2a2 2 0 01-2-2V6zm0 9a2 2 0 012-2h28a2 2 0 012 2v2a2 2 0 01-2 2H2a2 2 0 01-2-2v-2zm2 7a2 2 0 00-2 2v2a2 2 0 002 2h12a2 2 0 002-2v-2a2 2 0 00-2-2H2z'
        fill='#000'
      />
    ),
  },
  [IconType.ViewCards]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M2 4a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2H2zm0 14a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2H2zM18 6a2 2 0 012-2h10a2 2 0 012 2v6a2 2 0 01-2 2H20a2 2 0 01-2-2V6zm2 12a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2H20z'
        fill='#000'
      />
    ),
  },

  [IconType.ArrowLongLeft]: {
    viewBox: "0 0 36 32",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M13.11 6.009a1.5 1.5 0 10-2.22-2.018l-10 11a1.5 1.5 0 000 2.018l10 11a1.5 1.5 0 102.22-2.018L5.39 17.5H34a1.5 1.5 0 000-3H5.39l7.72-8.491z'
        fill='#000'
      />
    ),
  },
  [IconType.ArrowLongRight]: {
    viewBox: "0 0 36 32",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M25.11 3.991a1.5 1.5 0 10-2.22 2.018l7.72 8.491H2a1.5 1.5 0 000 3h28.61l-7.72 8.491a1.5 1.5 0 102.22 2.018l10-11a1.5 1.5 0 000-2.018l-10-11z'
        fill='#000'
      />
    ),
  },
  [IconType.ArrowLongLBRT]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M26.523 19.607a1.5 1.5 0 102.996-.143l-.707-14.849a1.5 1.5 0 00-1.427-1.427l-14.85-.707a1.5 1.5 0 10-.142 2.996l11.462.546-20.23 20.23a1.5 1.5 0 102.122 2.121l20.23-20.23.546 11.463z'
        fill='#000'
      />
    ),
  },
  [IconType.ArrowheadUp]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M6.009 21.11a1.5 1.5 0 11-2.018-2.22l11-10a1.5 1.5 0 012.018 0l11 10a1.5 1.5 0 11-2.018 2.22L16 12.027 6.009 21.11z'
        fill='#000'
      />
    ),
  },
  [IconType.ArrowheadDown]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M6.009 11.89a1.5 1.5 0 10-2.018 2.22l11 10a1.5 1.5 0 002.018 0l11-10a1.5 1.5 0 00-2.018-2.22L16 20.973 6.009 11.89z'
        fill='#000'
      />
    ),
  },
  [IconType.ArrowheadRight]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M14.11 3.991a1.5 1.5 0 10-2.22 2.018L20.973 16l-9.083 9.991a1.5 1.5 0 102.22 2.018l10-11a1.5 1.5 0 000-2.018l-10-11z'
        fill='#000'
      />
    ),
  },
  [IconType.ArrowheadLeft]: {
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M20.11 6.009a1.5 1.5 0 10-2.22-2.018l-10 11a1.5 1.5 0 000 2.018l10 11a1.5 1.5 0 102.22-2.018L11.027 16l9.083-9.991z'
        fill='#000'
      />
    ),
  },
  [IconType.ArrowReverseRight]: {
    viewBox: "0 0 30 26",
    path: (
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M21.121 1.003a1.5 1.5 0 10-2.242 1.994l5.78 6.503H8.5a8 8 0 000 16H9a1.5 1.5 0 100-3h-.5a5 5 0 110-10h16.16l-5.781 6.503a1.5 1.5 0 102.242 1.994l8-9a1.5 1.5 0 000-1.993l-8-9z'
        fill='#000'
      />
    ),
  },
};

interface IconStyledBoxProps {
  height: CSSProperties["height"];
  width: CSSProperties["width"];
  size: CSSProperties["width"];
  opacity: CSSProperties["opacity"];
  color: CSSProperties["color"];
  isHover: boolean;
}
const IconStyledBox = styled(Box)<IconStyledBoxProps>`
  display: flex;
  justify-items: center;
  align-items: center;
  user-select: none;
  -webkit-user-drag: none;

  height: ${({ height, size }) => height || size};
  width: ${({ width, size }) => width || size};
  min-width: ${({ width, size }) => width || size};
  opacity: ${({ opacity }) => opacity || "100%"};

  & svg {
    width: 100%;
    height: 100%;

    &:hover {
      cursor: ${({ isHover }) => (isHover ? "pointer" : "default")};
    }

    & path {
      transition: ${getCSSTransition(0.35, "fill")};
      fill: ${({ color }) => color ?? ""};
    }
  }
`;

export interface IconProps {
  type: IconType;
  color?: string;
  className?: string | undefined;
  onClick?: onClickHandler;
  height?: CSSProperties["height"];
  width?: CSSProperties["width"];
  size?: CSSProperties["width"];
  opacity?: CSSProperties["opacity"];
  style?: CSSProperties;
  forceSVGElement?: ReactNode;
  isHover?: boolean;
}

const Icon: React.FC<IconProps> = ({
  type,
  color,
  forceSVGElement = null,
  className = "",
  onClick,
  height = undefined,
  width = undefined,
  size = "16px",
  opacity = undefined,
  isHover = false,
  style = {},
}) => {
  const palette = useAppThemePalette();

  const icon = useMemo<Required<IconTypedProps>>(() => {
    return { viewBox: "0 0 32 32", ..._icons[type] };
  }, [type]);

  return (
    <IconStyledBox
      isHover={!!onClick || isHover}
      height={height}
      width={width}
      size={size}
      opacity={opacity}
      color={color ?? palette.common.black}
      style={style ?? {}}
      onClick={(e) => {
        e?.preventDefault();
        if (onClick) {
          onClick(e);
        }
      }}
      className={`${className ?? ""}`}>
      {forceSVGElement ? (
        forceSVGElement
      ) : (
        <SvgIcon viewBox={icon.viewBox}>{icon.path}</SvgIcon>
      )}
    </IconStyledBox>
  );
};

export default Icon;
